import {
  Box,
  Chip,
  Divider,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Toolbar,
  Typography,
} from '@mui/material';
import DashboardIcon from '@mui/icons-material/Dashboard';
import ElevatorIcon from '@mui/icons-material/Elevator';
import EventNoteIcon from '@mui/icons-material/EventNote';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import NextPlanIcon from '@mui/icons-material/NextPlan';
import RouterIcon from '@mui/icons-material/Router';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import TabletIcon from '@mui/icons-material/Tablet';
import HubIcon from '@mui/icons-material/Hub';
import type { ElementType } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { navigationItems } from '@/routes/navigationItems';
import type { NavGroup } from '@/routes/navigationItems';

export const DRAWER_WIDTH = 272;

const ICON_MAP: Record<string, ElementType> = {
  SmartToy: SmartToyIcon,
  Tablet: TabletIcon,
  HourglassEmpty: HourglassEmptyIcon,
  Elevator: ElevatorIcon,
  NextPlan: NextPlanIcon,
  Router: RouterIcon,
  EventNote: EventNoteIcon,
  Dashboard: DashboardIcon,
};

type GroupMeta = {
  readonly label: string;
  readonly badge: string;
  readonly badgeColor: 'info' | 'warning' | 'success';
};

const GROUP_META: Record<NavGroup, GroupMeta> = {
  crud: { label: '設定 (REST API + DB)', badge: 'CRUD', badgeColor: 'info' },
  sim: { label: 'MQTT シミュレータ', badge: 'SIM', badgeColor: 'warning' },
  runtime: { label: 'ダッシュボード', badge: 'MON', badgeColor: 'success' },
};

type NavigationDrawerProps = {
  readonly mobileOpen: boolean;
  readonly onMobileClose: () => void;
};

function DrawerContent({ onItemClick }: { readonly onItemClick?: () => void }) {
  const location = useLocation();
  const groups: NavGroup[] = ['runtime', 'sim', 'crud'];

  return (
    <>
      <Toolbar sx={{ px: 2.5 }}>
        <Stack direction="row" alignItems="center" spacing={1.25}>
          <Box
            sx={{
              width: 34,
              height: 34,
              borderRadius: 1.5,
              display: 'grid',
              placeItems: 'center',
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
            }}
          >
            <HubIcon fontSize="small" />
          </Box>
          <Box>
            <Typography variant="subtitle1" sx={{ lineHeight: 1.1 }}>
              HFI Web Console
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Ville-feuille HFI Server
            </Typography>
          </Box>
        </Stack>
      </Toolbar>
      <Divider />
      <Box sx={{ py: 1.5, overflowY: 'auto', flex: 1 }}>
        {groups.map((group, gIdx) => {
          const items = navigationItems.filter((i) => i.group === group);
          const meta = GROUP_META[group];
          return (
            <Box key={group} sx={{ mb: gIdx === groups.length - 1 ? 0 : 1.5 }}>
              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{ px: 2.5, mb: 0.75 }}
              >
                <Typography
                  variant="overline"
                  sx={{ color: 'text.secondary', fontSize: 10.5 }}
                >
                  {meta.label}
                </Typography>
                <Chip
                  label={meta.badge}
                  size="small"
                  color={meta.badgeColor}
                  variant="outlined"
                  sx={{ height: 18, fontSize: 9.5, '& .MuiChip-label': { px: 0.75 } }}
                />
              </Stack>
              <List disablePadding>
                {items.map((item) => {
                  const Icon = ICON_MAP[item.icon] ?? SmartToyIcon;
                  const selected = location.pathname.startsWith(item.path);
                  return (
                    <ListItem key={item.path} disablePadding sx={{ px: 1.25 }}>
                      <ListItemButton
                        component={NavLink}
                        to={item.path}
                        selected={selected}
                        onClick={onItemClick}
                        sx={{
                          borderRadius: 1.5,
                          py: 0.75,
                          '&.Mui-selected': {
                            bgcolor: 'rgba(37, 99, 235, 0.08)',
                            '&:hover': { bgcolor: 'rgba(37, 99, 235, 0.12)' },
                            '& .MuiListItemIcon-root, & .MuiListItemText-primary': {
                              color: 'primary.main',
                            },
                          },
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 34, color: 'text.secondary' }}>
                          <Icon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText
                          primary={item.text}
                          secondary={item.description}
                          primaryTypographyProps={{ fontSize: 13.5, fontWeight: 600 }}
                          secondaryTypographyProps={{ fontSize: 11.5, color: 'text.secondary' }}
                        />
                      </ListItemButton>
                    </ListItem>
                  );
                })}
              </List>
            </Box>
          );
        })}
      </Box>
    </>
  );
}

export function NavigationDrawer({ mobileOpen, onMobileClose }: NavigationDrawerProps) {
  return (
    <Box
      component="nav"
      sx={{ width: { md: DRAWER_WIDTH }, flexShrink: { md: 0 } }}
      aria-label="navigation"
    >
      {/* Mobile drawer (temporary) */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={onMobileClose}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { width: DRAWER_WIDTH, boxSizing: 'border-box' },
        }}
      >
        <DrawerContent onItemClick={onMobileClose} />
      </Drawer>
      {/* Desktop drawer (permanent) */}
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', md: 'block' },
          '& .MuiDrawer-paper': { width: DRAWER_WIDTH, boxSizing: 'border-box' },
        }}
        open
      >
        <DrawerContent />
      </Drawer>
    </Box>
  );
}
